/**
 * The four email templates from the CRM demo. Merge fields: {first} (contact's first name),
 * {company} (account name) and {me} (the signed-in user).
 */
export const EMAIL_TEMPLATES = {
    intro: {
        name: 'Intro',
        subject: 'Inspections and audits for {company}',
        body: 'Hi {first},\n\nThanks for your time. eAuditor helps teams like {company} run mobile inspections, fix issues with action plans, and report across every site.\n\nWould a 20-minute demo next week work?\n\nBest,\n{me}',
    },
    followup: {
        name: 'Proposal follow-up',
        subject: 'Following up on the {company} proposal',
        body: 'Hi {first},\n\nI wanted to check whether you had a chance to review the proposal. Happy to walk through pricing or the rollout plan.\n\nBest,\n{me}',
    },
    checkin: {
        name: 'Quarterly check-in',
        subject: 'Quarterly check-in: {company}',
        body: "Hi {first},\n\nIt's time for our quarterly check-in. I'd like to review inspection results, open action items, and anything we can improve.\n\nBest,\n{me}",
    },
    welcome: {
        name: 'Welcome / handoff',
        subject: 'Welcome to eAuditor, {company}',
        body: 'Hi {first},\n\nWelcome aboard! Your customer success manager will reach out to schedule the kick-off and your first inspection.\n\nBest,\n{me}',
    },
}

export const EMAIL_TEMPLATE_KEYS = Object.keys(EMAIL_TEMPLATES)

/** First word of a full name ("Ana Gomez" -> "Ana"); falls back to "there". */
export const firstName = fullName => String(fullName || '').trim().split(/\s+/)[0] || 'there'

/**
 * Replace {first}, {company} and {me}. Values are inserted as plain text (a "$&" in a name is not special)
 * and unknown {placeholders} are left alone.
 * @param {string} text
 * @param {{ first?: string, company?: string, me?: string }} values
 */
export const mergeTemplate = (text, { first = '', company = '', me = '' }) => {
    const values = { first, company, me }
    // One pass over the template, so text that came from a value (e.g. a name containing "{me}") is never expanded again.
    return String(text ?? '').replace(/\{(first|company|me)\}/g, (_, key) => values[key])
}

/**
 * Subject and body for a template, merged for one contact.
 * @param {keyof typeof EMAIL_TEMPLATES} key
 * @param {{ contactName?: string, company?: string, me?: string }} who
 */
export const fillTemplate = (key, { contactName, company, me }) => {
    const template = EMAIL_TEMPLATES[key]
    const values = { first: firstName(contactName), company: company || '', me: me || '' }
    return { subject: mergeTemplate(template.subject, values), body: mergeTemplate(template.body, values) }
}

/**
 * mailto: link. Everything is percent-encoded, so line breaks in the body stay line breaks and nothing
 * can add extra headers.
 */
export const buildMailto = ({ to, subject, body }) =>
    `mailto:${encodeURIComponent(String(to || '').trim())}?subject=${encodeURIComponent(subject || '')}&body=${encodeURIComponent(body || '')}`
